// src/hooks/useDashboardData.ts
import { useState, useEffect } from 'react';
import { dashboardService } from '../services/dashboardService';
import type { ProjectInfo } from '../types/dashboard';

/**
 * Hook for loading project-specific data only.
 * Global/user data (profile, layers, beta, alerts, sideMenu, etc.) is already loaded
 * by MainLayout and should NOT be re-fetched here.
 *
 * Do not send dashboard group/totals requests from here: they carry IsFilterPersonal=false
 * and the backend persists their Filters as the dashboard's global (Apply For All) filters.
 * Sending them on project switch with empty Filters wiped the user's shared filters.
 */
export const useDashboardData = (projectId: number = 35) =>
{
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Only project-specific state
  const [projectInfo, setProjectInfo] = useState<ProjectInfo | null>(null);

  useEffect(() =>
  {
    loadDashboardData();
  }, [projectId]);

  const loadDashboardData = async () =>
  {
    try
    {
      setLoading(true);
      setError(null);

      const projectData = await dashboardService.getProject(projectId);
      setProjectInfo(projectData);
      setLoading(false);
    }
    catch (err)
    {
      console.error('[useDashboardData] ❌ Error loading dashboard data:', err);
      setError(err instanceof Error ? err.message : 'Error loading dashboard data');
      setLoading(false);
    }
  };

  return {
    loading,
    error,
    projectInfo,
    reload: loadDashboardData,
  };
};
